import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import FileDropZone from "components/FileDropZone";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { useDispatch, useSelector } from "react-redux";
import addURLPrefix from "utils/addURLPrefix";
import {
  createPaymentTypeThunk,
  updatePaymentTypeThunk,
} from "store/reducers/payment/paymentSlice";
import * as yup from "yup";
import SimpleBarScroll from "components/SimpleBar";
import SimpleBarHorizontal from "components/SimpleBarHorizontal";

const validation = yup.object({
  name: yup.string().required("Name required"),
  desc: yup.string(),
  code: yup.string().required("Code required"),
  image: yup.mixed("Add an image").nullable(),
});

const dynamicFormInitials = {
  name: "",
  type: "text",
  required: false,
};

const PaymentTypeCreate = ({ paymentType = null, fullGrid = false }) => {
  const dispatch = useDispatch();
  const formik = useFormik({
    initialValues: {
      name: paymentType?.name ?? "",
      desc: paymentType?.desc ?? "",
      code: paymentType?.code ?? "",
      image: null,
    },
    validationSchema: validation,
    onSubmit: (data) => {
      const newData = { ...data, credential_format: JSON.stringify(credentialFormat) };
      if (paymentType?.image && !clearImage) {
        delete newData["image"];
      }
      console.log(newData);
      if (paymentType) {
        dispatch(updatePaymentTypeThunk({ id: paymentType.id, data: newData }));
      } else {
        dispatch(createPaymentTypeThunk(newData));
      }
    },
    validateOnChange: false,
  });

  const [files, setFiles] = useState([]);
  const { status } = useSelector((state) => state.paymentType);
  const [clearImage, setClearImage] = useState(false);

  useEffect(() => {
    formik.setFieldValue("image", files[0] ?? null);
  }, [files]);
  const isFullWidth = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const [backDropImage, setBackDropImage] = React.useState(null);
  const [openBackDrop, setOpenBackDrop] = React.useState(false);
  const [credentialFormat, setCredentialFormat] = React.useState([]);
  const [showCredentialForm, setShowCredentialForm] = React.useState(false);
  const [dynamicFormState, setDynamicFormState] =
    React.useState(dynamicFormInitials);
  const [dynamicFormErrors, setDynamicFormErrors] = React.useState({});

  const handleFormChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setDynamicFormState({ ...dynamicFormState, [name]: value });
  };

  const onImageClick = (image) => {
    setBackDropImage(image);
    setOpenBackDrop(true);
  };
  const handleBackdropClose = () => {
    setOpenBackDrop(false);
  };

  useEffect(() => {
    if (status?.error === "create" || status?.error === "update") {
      Object.keys(status?.errorMessage).forEach((key) => {
        console.log(key, status?.errorMessage?.create[key][0]);
        formik.setFieldError(key, status?.errorMessage?.create[key][0]);
      });
    }
  }, [status]);

  useEffect(() => {
    if (paymentType) {
      setCredentialFormat(paymentType.credential_format);
    }
  }, [paymentType]);

  const removeCredentialFormat = (i) => {
    setCredentialFormat((state) => state.filter((item, index) => index !== i));
  };

  const addCredentialItem = (data) => {
    if (data.name) {
      setDynamicFormErrors({});
      if(credentialFormat){
        setCredentialFormat([...credentialFormat, data]);
      }else{
        setCredentialFormat([data]);
      }
     
    } else {
      setDynamicFormErrors({ name: "Field required" });
    }
  };

  return (
    <>
      <SimpleBarScroll>
        <form onSubmit={formik.handleSubmit}>
          <Grid container columnSpacing={2} rowSpacing={0}>
            <Grid item xs={12} md={fullGrid ? 12 : 6} lg={fullGrid ? 12 : 4}>
              <TextField
                label="Name"
                variant="outlined"
                name="name"
                fullWidth
                sx={{ mt: 3 }}
                value={formik.values.name}
                onChange={formik.handleChange}
                error={Boolean(formik.errors.name)}
                helperText={formik.errors.name}
                autoComplete="new-password"
                required
              />
            </Grid>
            <Grid item xs={12} md={fullGrid ? 12 : 6} lg={fullGrid ? 12 : 4}>
              <TextField
                label="Description"
                variant="outlined"
                name="desc"
                fullWidth
                sx={{ mt: 3 }}
                value={formik.values.desc}
                onChange={formik.handleChange}
                error={Boolean(formik.errors.desc)}
                helperText={formik.errors.desc}
                autoComplete="new-password"
              />
            </Grid>
            <Grid item xs={12} md={fullGrid ? 12 : 6} lg={fullGrid ? 12 : 4}>
              <TextField
                label="Code"
                variant="outlined"
                name="code"
                fullWidth
                sx={{ mt: 3 }}
                value={formik.values.code}
                onChange={formik.handleChange}
                error={Boolean(formik.errors.code)}
                helperText={formik.errors.code}
                autoComplete="new-password"
                required
              />
            </Grid>

            <Grid item xs={12} md={fullGrid ? 12 : 6} lg={fullGrid ? 12 : 4}>
              <Box>
                <Typography variant="h6" color="gray" marginTop={2}>
                  Choose a photo
                </Typography>
                {paymentType?.image && !clearImage ? (
                  <>
                    <Typography marginY={2} color="text.secondary">
                      PaymentType Image
                    </Typography>
                    <Paper
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        padding: 0.5,
                        position: "relative",
                        marginLeft: 1,
                      }}
                    >
                      <LazyLoadImage
                        alt="machine image"
                        src={addURLPrefix(paymentType?.image)}
                        height={200}
                        width={200}
                        style={{ objectFit: "cover", borderRadius: 4 }}
                        onClick={() =>
                          onImageClick(addURLPrefix(paymentType?.image))
                        }
                      />
                    </Paper>
                    <Box>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setClearImage(true)}
                        sx={{ margin: 1 }}
                        fullWidth={false}
                      >
                        Remove image
                      </Button>
                    </Box>
                  </>
                ) : (
                  <FileDropZone
                    maxFiles={1}
                    accept={{
                      "image/png": [".png"],
                      "image/jpeg": [".jpg", ".jpeg"],
                    }}
                    mode="image"
                    files={files}
                    setFiles={setFiles}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
          <Stack direction="column">
            <Button
              variant="outlined"
              onClick={() => setShowCredentialForm((state) => !state)}
              sx={{ width: 200 }}
            >
              {showCredentialForm
                ? "Hide credential form"
                : "show credential form"}
            </Button>
            {credentialFormat?.length > 0 && showCredentialForm && (
              <SimpleBarHorizontal>
                <Box
                  sx={{
                    backgroundColor: "#e7ebf0",
                    borderRadius: 2,
                    flex: 1,
                    mt: 2,
                  }}
                  padding={1}
                >
                  <TableContainer component={Paper}>
                    <Table aria-label="simple table">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ minWidth: 100 }}>Name</TableCell>
                          <TableCell align="center" sx={{ minWidth: 80 }}>
                            Type
                          </TableCell>
                          <TableCell align="center" sx={{ minWidth: 80 }}>
                            Required
                          </TableCell>
                          <TableCell>&nbsp;</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {credentialFormat.map((cred, index) => (
                          <TableRow
                            key={index}
                            sx={{
                              "&:last-child td, &:last-child th": { border: 0 },
                            }}
                          >
                            <TableCell component="th" scope="row">
                              {cred.name}
                            </TableCell>
                            <TableCell align="center">{cred.type}</TableCell>
                            <TableCell align="center">
                              {cred.required ? "Yes" : "No"}
                            </TableCell>

                            <TableCell align="center">
                              <LoadingButton
                                variant="outlined"
                                color="error"
                                onClick={() => {
                                  removeCredentialFormat(index);
                                }}
                              >
                                Delete
                              </LoadingButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </SimpleBarHorizontal>
            )}

            {showCredentialForm && (
              <Grid container columnSpacing={2} rowSpacing={0}>
                <Grid item xs={12} md={6} lg={4}>
                  <TextField
                    label="Name"
                    variant="outlined"
                    name="name"
                    fullWidth
                    sx={{ mt: 2 }}
                    value={dynamicFormState?.name}
                    onChange={handleFormChange}
                    autoComplete="new-password"
                    error={Boolean(dynamicFormErrors?.name)}
                    helperText={dynamicFormErrors?.name}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <TextField
                    label="Type"
                    variant="outlined"
                    name="type"
                    select
                    fullWidth
                    sx={{ mt: 2 }}
                    value={dynamicFormState?.type}
                    error={Boolean(dynamicFormErrors?.type)}
                    helperText={dynamicFormErrors?.type}
                    onChange={handleFormChange}
                    autoComplete="new-password"
                    required
                  >
                    <MenuItem value="text">Text</MenuItem>
                    <MenuItem value="number">Number</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <TextField
                    label="Required"
                    variant="outlined"
                    name="required"
                    select
                    fullWidth
                    sx={{ mt: 2 }}
                    value={dynamicFormState?.required}
                    onChange={handleFormChange}
                    autoComplete="new-password"
                    error={Boolean(dynamicFormErrors?.required)}
                    helperText={dynamicFormErrors?.required}
                  >
                    <MenuItem value={true}>Yes</MenuItem>
                    <MenuItem value={false}>No</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      addCredentialItem(dynamicFormState);
                    }}
                    sx={{ mt: 2 }}
                  >
                    Add
                  </Button>
                </Grid>
              </Grid>
            )}

            {((!paymentType && status?.error === "create") ||
              (paymentType && status?.error === "update")) && (
              <Alert sx={{ my: 1 }} severity="error">
                Error occurred
              </Alert>
            )}

            {((!paymentType && status?.success === "create") ||
              (paymentType && status?.success === "success")) && (
              <Alert sx={{ my: 1 }} severity="success">
                Success
              </Alert>
            )}
            <LoadingButton
              loading={
                (!paymentType && status?.loading === "create") ||
                (paymentType && status?.loading === "update")
              }
              variant="contained"
              fullWidth={isFullWidth}
              type="submit"
              sx={{ paddingX: 10, marginTop: 1, width: 200 }}
            >
              {paymentType ? "Update" : "Create"}
            </LoadingButton>
          </Stack>
        </form>
      </SimpleBarScroll>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={openBackDrop}
        onClick={handleBackdropClose}
      >
        <LazyLoadImage
          alt="machine image"
          src={backDropImage}
          height="95%"
          width="95%"
          style={{ objectFit: "contain" }}
        />
      </Backdrop>
    </>
  );
};

export default PaymentTypeCreate;
